// Pure geometry helpers for moving and resizing canvas elements.

import { hasCornerRadius, roundedPolygonPath, parsePoints, resolveCornerRadius } from "./cornerRadius";

// ---- Rotation helpers ----
export function rotationOf(el) {
  return Number(el?.attrs?.rotation) || 0;
}

export function centerOf(bb) {
  return { x: bb.x + bb.w / 2, y: bb.y + bb.h / 2 };
}

// Rotate a vector by `deg` degrees (no pivot).
export function rotateVec(v, deg) {
  const r = (deg * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  return { x: v.x * cos - v.y * sin, y: v.x * sin + v.y * cos };
}

// Rotate point `p` around pivot `c` by `deg` degrees.
export function rotatePoint(p, c, deg) {
  const v = rotateVec({ x: p.x - c.x, y: p.y - c.y }, deg);
  return { x: c.x + v.x, y: c.y + v.y };
}

// ---- Translation (drag) ----
export function translateElement(el, dx, dy) {
  const a = { ...el.attrs };
  switch (el.type) {
    case "rect":
    case "text":
      a.x = (el.attrs.x ?? 0) + dx;
      a.y = (el.attrs.y ?? 0) + dy;
      break;
    case "image":
      a.x = (el.attrs.x ?? 0) + dx;
      a.y = (el.attrs.y ?? 0) + dy;
      // Keep the original-image box in sync so crop mode can restore it.
      if (el.attrs.origX != null) a.origX = (el.attrs.origX ?? 0) + dx;
      if (el.attrs.origY != null) a.origY = (el.attrs.origY ?? 0) + dy;
      break;
    case "circle":
    case "ellipse":
      a.cx = (el.attrs.cx ?? 0) + dx;
      a.cy = (el.attrs.cy ?? 0) + dy;
      break;
    case "line":
      a.x1 = (el.attrs.x1 ?? 0) + dx;
      a.y1 = (el.attrs.y1 ?? 0) + dy;
      a.x2 = (el.attrs.x2 ?? 0) + dx;
      a.y2 = (el.attrs.y2 ?? 0) + dy;
      break;
    case "polygon":
    case "polyline":
      a.points = translatePoints(el.attrs.points, dx, dy);
      break;
    case "path":
      a.d = translatePath(el.attrs.d, dx, dy);
      break;
    default:
      break;
  }
  return a;
}

function translatePoints(points, dx, dy) {
  return String(points)
    .trim()
    .split(/\s+/)
    .map((p) => {
      const [px, py] = p.split(",").map(Number);
      return `${px + dx},${py + dy}`;
    })
    .join(" ");
}

// Best-effort translation of an SVG path's absolute commands.
function translatePath(d, dx, dy) {
  if (!d) return d;
  return d.replace(/([MLTQCSAHVZmltqcsahvz])([^MLTQCSAHVZmltqcsahvz]*)/g, (full, cmd, args) => {
    if (cmd === "Z" || cmd === "z") return cmd;
    const nums = args.trim().split(/[\s,]+/).filter(Boolean).map(Number);
    const upper = cmd === cmd.toUpperCase();
    // Only translate absolute single-coord commands; relative (lowercase) need no translation.
    if (!upper) return full;
    let out = "";
    if (cmd === "H") {
      out = nums.map((n) => n + dx).join(" ");
    } else if (cmd === "V") {
      out = nums.map((n) => n + dy).join(" ");
    } else if (cmd === "A") {
      // M rx ry x-axis large sweep x y. Only last two are coords.
      const groups = [];
      for (let i = 0; i + 6 < nums.length + 1; i += 7) {
        const g = nums.slice(i, i + 7);
        if (g.length === 7) {
          g[5] += dx;
          g[6] += dy;
          groups.push(g.join(" "));
        }
      }
      out = groups.join(" ");
    } else {
      // M L T : every 2 are coords. Q S : every 4 (2 pairs). C : every 6 (3 pairs).
      const result = [];
      for (let i = 0; i < nums.length; i += 2) {
        const x = (nums[i] ?? 0) + dx;
        const y = (nums[i + 1] ?? 0) + dy;
        result.push(`${x} ${y}`);
      }
      out = result.join(" ");
    }
    return `${cmd} ${out} `;
  });
}

// ---- Multi-select geometry helpers ----
// Union bounding box of several elements (artboard coordinates).
export function unionBbox(els) {
  const boxes = (els || []).map(bboxOf);
  if (!boxes.length) return { x: 0, y: 0, w: 0, h: 0 };
  const minX = Math.min(...boxes.map((b) => b.x));
  const minY = Math.min(...boxes.map((b) => b.y));
  const maxX = Math.max(...boxes.map((b) => b.x + b.w));
  const maxY = Math.max(...boxes.map((b) => b.y + b.h));
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

// Axis-aligned rectangle intersection test (marquee selection).
export function rectsIntersect(a, b) {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

// ---- Bounding box (in artboard coordinates) ----
export function bboxOf(el) {
  const a = el.attrs || {};
  switch (el.type) {
    case "rect":
    case "image":
      return { x: a.x ?? 0, y: a.y ?? 0, w: a.width ?? 0, h: a.height ?? 0 };
    case "circle": {
      const r = a.r ?? 0;
      return { x: (a.cx ?? 0) - r, y: (a.cy ?? 0) - r, w: r * 2, h: r * 2 };
    }
    case "ellipse": {
      return { x: (a.cx ?? 0) - (a.rx ?? 0), y: (a.cy ?? 0) - (a.ry ?? 0), w: (a.rx ?? 0) * 2, h: (a.ry ?? 0) * 2 };
    }
    case "line": {
      const x = Math.min(a.x1 ?? 0, a.x2 ?? 0);
      const y = Math.min(a.y1 ?? 0, a.y2 ?? 0);
      const w = Math.abs((a.x2 ?? 0) - (a.x1 ?? 0));
      const h = Math.abs((a.y2 ?? 0) - (a.y1 ?? 0));
      return { x, y, w, h };
    }
    case "polygon":
    case "polyline":
      // When a polygon has a corner radius it is rendered as a rounded <path>
      // (see ElementRenderer). The rounded path's bbox is SMALLER than the
      // raw vertices' bbox because the sharp tips are trimmed into arcs, so we
      // must measure the actual rounded path — otherwise the selection frame
      // and resize handles sit outside the visible shape.
      if (el.type === "polygon" && hasCornerRadius(a) && a.points) {
        const pts = parsePoints(a.points);
        if (pts.length >= 3) {
          const raw = polyBbox(a.points);
          const r = resolveCornerRadius(a, raw.w, raw.h);
          if (r > 0) {
            const d = roundedPolygonPath(pts, r);
            const measured = pathBbox(d);
            if (measured && (measured.w || measured.h)) return measured;
          }
        }
      }
      return polyBbox(a.points);
    case "path":
      return pathBbox(a.d);
    case "text": {
      const m = measureTextBBox(a);
      if (m && (m.w || m.h)) {
        const anchor = a.textAnchor || "start";
        const off = anchor === "middle" ? m.w / 2 : anchor === "end" ? m.w : 0;
        return { x: (a.x ?? 0) + m.x - off, y: (a.y ?? 0) + m.y, w: m.w, h: m.h };
      }
      // Fallback heuristic (no DOM): assume ~full-width advance so the box
      // never under-shoots CJK / wide glyphs the way the old 0.55 factor did.
      const size = a.fontSize ?? 32;
      const txt = a.text || "";
      const w = txt.length * size * 0.62;
      return { x: a.x ?? 0, y: (a.y ?? 0) - size * 0.85, w, h: size * 1.2 };
    }
    default:
      return { x: 0, y: 0, w: 0, h: 0 };
  }
}

function polyBbox(points) {
  if (!points) return { x: 0, y: 0, w: 0, h: 0 };
  const xs = [];
  const ys = [];
  String(points)
    .trim()
    .split(/\s+/)
    .forEach((p) => {
      const [x, y] = p.split(",").map(Number);
      if (!Number.isNaN(x)) xs.push(x);
      if (!Number.isNaN(y)) ys.push(y);
    });
  if (!xs.length) return { x: 0, y: 0, w: 0, h: 0 };
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

// Accurate path bounding box via the browser's native getBBox(). A single
// offscreen <svg><path/></svg> is created lazily and reused, so this is fast
// even when called repeatedly (marquee selection, status bar, etc.).
// The previous numeric heuristic paired EVERY number in the `d` string, which
// mis-parsed arc commands (`A rx ry x-rot large sweep x y`) — treating radii
// and flags as coordinates and producing wildly oversized boxes. That made the
// selection / marquee frame of ring loaders explode far beyond the viewport.
// Small memo cache so the same geometry isn't re-measured (getBBox forces a
// synchronous reflow). During a drag the selection overlays recompute the
// element bbox every animation frame; for text that re-shaped CJK/custom-font
// glyphs AND forced a layout each frame right after the <text> mutation — the
// classic read-after-write thrash that made text dragging lag. The text bbox
// is identical while only x/y change, so caching by content signature makes
// bboxOf() pure math during a move.
const _pathBBoxCache = new Map();
const _textBBoxCache = new Map();
function _cacheGet(cache, key) {
  return cache.has(key) ? cache.get(key) : undefined;
}
function _cacheSet(cache, key, val) {
  if (cache.size > 600) cache.clear();
  cache.set(key, val);
  return val;
}

let _measureSvg = null;
let _measurePath = null;
function measurePathBBox(d) {
  if (typeof document === "undefined") return null;
  const cached = _cacheGet(_pathBBoxCache, d);
  if (cached !== undefined) return cached;
  if (!_measureSvg) {
    const NS = "http://www.w3.org/2000/svg";
    _measureSvg = document.createElementNS(NS, "svg");
    _measureSvg.setAttribute("width", "0");
    _measureSvg.setAttribute("height", "0");
    _measureSvg.setAttribute("aria-hidden", "true");
    _measureSvg.style.cssText = "position:absolute;left:-99999px;top:-99999px;width:0;height:0;overflow:hidden;pointer-events:none;";
    _measurePath = document.createElementNS(NS, "path");
    _measureSvg.appendChild(_measurePath);
    (document.body || document.documentElement).appendChild(_measureSvg);
  }
  try {
    _measurePath.setAttribute("d", d);
    const b = _measurePath.getBBox();
    if (!b || (!b.width && !b.height && !b.x && !b.y)) return _cacheSet(_pathBBoxCache, d, null);
    return _cacheSet(_pathBBoxCache, d, { x: b.x, y: b.y, w: b.width, h: b.height });
  } catch {
    return null;
  }
}

function numericPathBbox(d) {
  // Fallback only (no DOM): approximate using number pairs in M/L/C/Q/T,
  // skipping the parameters of arc (A) commands which are not coordinates.
  if (!d) return { x: 0, y: 0, w: 0, h: 0 };
  const xs = [];
  const ys = [];
  const re = /([MLTQCSAHVZmltqcsahvz])([^MLTQCSAHVZmltqcsahvz]*)/g;
  let m;
  while ((m = re.exec(d))) {
    const cmd = m[1];
    if (cmd === "Z" || cmd === "z") continue;
    const nums = (m[2].trim().match(/-?\d*\.?\d+(?:e[-+]?\d+)?/gi) || []).map(Number);
    const upper = cmd === cmd.toUpperCase();
    if (!upper) continue;
    if (cmd === "H") nums.forEach((n) => xs.push(n));
    else if (cmd === "V") nums.forEach((n) => ys.push(n));
    else if (cmd === "A") {
      for (let i = 0; i + 6 < nums.length + 1; i += 7) {
        if (nums.length >= i + 7) { xs.push(nums[i + 5]); ys.push(nums[i + 6]); }
      }
    } else {
      for (let i = 0; i + 1 < nums.length; i += 2) { xs.push(nums[i]); ys.push(nums[i + 1]); }
    }
  }
  if (!xs.length) return { x: 0, y: 0, w: 0, h: 0 };
  return { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
}

function pathBbox(d) {
  if (!d) return { x: 0, y: 0, w: 0, h: 0 };
  return measurePathBBox(d) || numericPathBbox(d);
}

// Accurate text bounding box via the browser's native getBBox(). Mirrors the
// path measurement: a single offscreen <svg><text/></svg> is created lazily and
// reused. The previous heuristic (charCount * fontSize * 0.55) badly under-sized
// full-width CJK glyphs, so the selection frame only covered the left portion of
// the text. getBBox measures the real shaped glyphs for any script + font.
let _measureTextSvg = null;
let _measureTextNode = null;
function measureTextBBox(a) {
  if (typeof document === "undefined") return null;
  const size = a.fontSize ?? 32;
  const sig = `${size}|${a.fontFamily || "IBM Plex Sans"}|${a.fontWeight || 400}|${a.fontStyle || "normal"}|${a.letterSpacing ?? ""}|${a.text || ""}`;
  const cached = _cacheGet(_textBBoxCache, sig);
  if (cached !== undefined) return cached;
  if (!_measureTextSvg) {
    const NS = "http://www.w3.org/2000/svg";
    _measureTextSvg = document.createElementNS(NS, "svg");
    _measureTextSvg.setAttribute("aria-hidden", "true");
    _measureTextSvg.style.cssText = "position:absolute;left:-99999px;top:-99999px;width:0;height:0;overflow:hidden;pointer-events:none;";
    _measureTextNode = document.createElementNS(NS, "text");
    _measureTextNode.setAttribute("x", "0");
    _measureTextNode.setAttribute("y", "0");
    _measureTextSvg.appendChild(_measureTextNode);
    (document.body || document.documentElement).appendChild(_measureTextSvg);
  }
  try {
    const size = a.fontSize ?? 32;
    _measureTextNode.setAttribute("font-size", String(size));
    _measureTextNode.setAttribute("font-family", a.fontFamily || "IBM Plex Sans");
    _measureTextNode.setAttribute("font-weight", String(a.fontWeight || 400));
    _measureTextNode.setAttribute("font-style", a.fontStyle || "normal");
    if (a.letterSpacing != null && a.letterSpacing !== "") _measureTextNode.setAttribute("letter-spacing", String(a.letterSpacing));
    else _measureTextNode.removeAttribute("letter-spacing");
    _measureTextNode.textContent = a.text || "";
    const b = _measureTextNode.getBBox();
    if (!b || (!b.width && !b.height)) return _cacheSet(_textBBoxCache, sig, null);
    return _cacheSet(_textBBoxCache, sig, { x: b.x, y: b.y, w: b.width, h: b.height });
  } catch {
    return null;
  }
}

// ---- Resize: scale element from bbox space (sx, sy around origin ox, oy) ----
export function resizeElement(el, original, scaleX, scaleY, ox, oy) {
  const a = { ...original.attrs };
  const sx = scaleX === 0 ? 0.001 : scaleX;
  const sy = scaleY === 0 ? 0.001 : scaleY;
  const tX = (x) => ox + (x - ox) * sx;
  const tY = (y) => oy + (y - oy) * sy;

  switch (el.type) {
    case "rect": {
      const nx = tX(a.x ?? 0);
      const ny = tY(a.y ?? 0);
      const nx2 = tX((a.x ?? 0) + (a.width ?? 0));
      const ny2 = tY((a.y ?? 0) + (a.height ?? 0));
      a.x = Math.min(nx, nx2);
      a.y = Math.min(ny, ny2);
      a.width = Math.max(1, Math.abs(nx2 - nx));
      a.height = Math.max(1, Math.abs(ny2 - ny));
      break;
    }
    case "image": {
      const nx = tX(a.x ?? 0);
      const ny = tY(a.y ?? 0);
      const nx2 = tX((a.x ?? 0) + (a.width ?? 0));
      const ny2 = tY((a.y ?? 0) + (a.height ?? 0));
      a.x = Math.min(nx, nx2);
      a.y = Math.min(ny, ny2);
      a.width = Math.max(1, Math.abs(nx2 - nx));
      a.height = Math.max(1, Math.abs(ny2 - ny));
      // Scale the original-image box by the same factors so crop mode can
      // restore the full source image at the new position/size. cropX/Y/W/H
      // are 0–1 fractions of the orig box, so they stay valid after scaling.
      if (a.origX != null) {
        const onx = tX(a.origX);
        const ony = tY(a.origY);
        const onx2 = tX(a.origX + (a.origW ?? 0));
        const ony2 = tY(a.origY + (a.origH ?? 0));
        a.origX = Math.min(onx, onx2);
        a.origY = Math.min(ony, ony2);
        a.origW = Math.max(1, Math.abs(onx2 - onx));
        a.origH = Math.max(1, Math.abs(ony2 - ony));
      }
      break;
    }
    case "circle": {
      // A circle resized non-uniformly becomes an ellipse so that side handles
      // can squash/stretch it and shrink a single axis. Uniform scaling (corner
      // drag with equal factors) keeps it a perfect circle.
      const cx = tX(a.cx ?? 0);
      const cy = tY(a.cy ?? 0);
      const asx = Math.abs(sx);
      const asy = Math.abs(sy);
      const r0 = a.r ?? 0;
      if (Math.abs(asx - asy) < 1e-4) {
        a.cx = cx;
        a.cy = cy;
        a.r = Math.max(1, r0 * asx);
      } else {
        delete a.r;
        a.cx = cx;
        a.cy = cy;
        a.rx = Math.max(1, r0 * asx);
        a.ry = Math.max(1, r0 * asy);
        a.__type = "ellipse";
      }
      break;
    }
    case "ellipse": {
      a.cx = tX(a.cx ?? 0);
      a.cy = tY(a.cy ?? 0);
      a.rx = Math.max(1, (a.rx ?? 0) * Math.abs(sx));
      a.ry = Math.max(1, (a.ry ?? 0) * Math.abs(sy));
      break;
    }
    case "line": {
      a.x1 = tX(a.x1 ?? 0);
      a.y1 = tY(a.y1 ?? 0);
      a.x2 = tX(a.x2 ?? 0);
      a.y2 = tY(a.y2 ?? 0);
      break;
    }
    case "polygon":
    case "polyline": {
      a.points = String(a.points || "")
        .trim()
        .split(/\s+/)
        .map((p) => {
          const [px, py] = p.split(",").map(Number);
          return `${tX(px)},${tY(py)}`;
        })
        .join(" ");
      break;
    }
    case "text": {
      a.x = tX(a.x ?? 0);
      a.y = tY(a.y ?? 0);
      a.fontSize = Math.max(4, (a.fontSize ?? 32) * Math.max(Math.abs(sx), Math.abs(sy)));
      break;
    }
    case "path": {
      a.d = scalePath(a.d, sx, sy, ox, oy);
      break;
    }
    default:
      break;
  }
  return a;
}

function scalePath(d, sx, sy, ox, oy) {
  if (!d) return d;
  return d.replace(/([MLTQCSAHVZmltqcsahvz])([^MLTQCSAHVZmltqcsahvz]*)/g, (full, cmd, args) => {
    if (cmd === "Z" || cmd === "z") return cmd;
    const nums = args.trim().split(/[\s,]+/).filter(Boolean).map(Number);
    const isAbs = cmd === cmd.toUpperCase();
    if (!isAbs) return full;
    let out = "";
    const tX = (x) => ox + (x - ox) * sx;
    const tY = (y) => oy + (y - oy) * sy;
    if (cmd === "H") {
      out = nums.map((n) => tX(n)).join(" ");
    } else if (cmd === "V") {
      out = nums.map((n) => tY(n)).join(" ");
    } else if (cmd === "A") {
      const groups = [];
      for (let i = 0; i + 6 < nums.length + 1; i += 7) {
        const g = nums.slice(i, i + 7);
        if (g.length === 7) {
          g[0] = g[0] * Math.abs(sx);
          g[1] = g[1] * Math.abs(sy);
          g[5] = tX(g[5]);
          g[6] = tY(g[6]);
          groups.push(g.join(" "));
        }
      }
      out = groups.join(" ");
    } else {
      const result = [];
      for (let i = 0; i < nums.length; i += 2) {
        result.push(`${tX(nums[i] ?? 0)} ${tY(nums[i + 1] ?? 0)}`);
      }
      out = result.join(" ");
    }
    return `${cmd} ${out} `;
  });
}
